import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { Loader2, Upload, Layers, AlertCircle, CheckCircle2, Sparkles, Zap } from "lucide-react";
import { toast } from "sonner";
import { compressVideoForUpload, isCompressionSupported } from "@/lib/plyxVideoCompress";

/**
 * Plyx Duet creation page — `/plyx/duet/:videoId`.
 *
 * UX:
 *   1. Show original video preview + creator chip.
 *   2. User picks a remix clip (any video file).
 *   3. If the clip is bigger than ~40MB AND the browser supports
 *      WebCodecs/MediaRecorder, transcode it to ~720p in-browser
 *      before upload (iter-119q). A 180MB phone clip routinely
 *      shrinks to 25-40MB after this pass, comfortably under the
 *      nginx 100MB ingress cap.
 *   4. Upload → backend queues ffmpeg compositing.
 *   5. Poll the new video's `duet_status` every 3s. When "ready", redirect
 *      to /plyx/feed (the duet will appear at the top).
 */
// iter-119p: nginx ingress in front of plyx.co.uk caps request bodies
// at ~100MB. iter-119q: instead of rejecting big clips outright, we
// run an in-browser transcode so most phone uploads "just work".
// PLYX_DUET_HARD_MAX is the size we'll accept before compressing.
// PLYX_DUET_UPLOAD_CAP is the ceiling AFTER compression — if the
// transcoded blob is still over this we error out with an actionable
// message.
const PLYX_DUET_COMPRESS_THRESHOLD = 40 * 1024 * 1024;  // 40MB
const PLYX_DUET_UPLOAD_CAP = 95 * 1024 * 1024;          // 95MB (under 100MB ingress)
const PLYX_DUET_HARD_MAX = 500 * 1024 * 1024;           // 500MB raw

const formatMB = (bytes) => `${(bytes / 1024 / 1024).toFixed(1)} MB`;

const PlyxDuet = () => {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = usePlyxAuth();
  const [parent, setParent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [duetVideoId, setDuetVideoId] = useState(null);
  const [duetStatus, setDuetStatus] = useState(null);
  // iter-119q: in-browser compression progress (0..1)
  const [compressing, setCompressing] = useState(false);
  const [compressProgress, setCompressProgress] = useState(0);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/plyx/login", { replace: true, state: { from: `/plyx/duet/${videoId}` } });
      return;
    }
    api.get(`/plyx/videos/${videoId}`)
      .then((r) => {
        if (r.data.duets_enabled === false) {
          setError("The creator disabled duets on this video.");
        } else if (r.data.creator_id === user.plyx_user_id) {
          setError("You can't duet your own video.");
        } else {
          setParent(r.data);
        }
      })
      .catch((e) => setError(e.response?.data?.detail || "Could not load video"))
      .finally(() => setLoading(false));
  }, [videoId, user, authLoading, navigate]);

  // Poll status while composing
  useEffect(() => {
    if (!duetVideoId || duetStatus === "ready" || duetStatus === "failed") return;
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/plyx/videos/${duetVideoId}`);
        setDuetStatus(r.data.duet_status);
        if (r.data.duet_status === "ready") {
          toast.success("Your duet is live!");
          setTimeout(() => navigate("/plyx/feed"), 1200);
        } else if (r.data.duet_status === "failed") {
          toast.error("Composing failed — try a shorter clip");
        }
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(t);
  }, [duetVideoId, duetStatus, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Pick a video clip first"); return; }
    // iter-119q hard guard. We can transcode up to ~500MB on most
    // phones, but going much larger risks running out of memory on
    // older Androids. Reject with an actionable message.
    if (file.size > PLYX_DUET_HARD_MAX) {
      toast.error(
        `Your clip is ${formatMB(file.size)} — too large even after compression. Trim a shorter cut (under ~3 min) and try again. Max ${formatMB(PLYX_DUET_HARD_MAX)}.`,
        { duration: 7000 },
      );
      return;
    }

    // iter-119q: compress in-browser BEFORE uploading if the clip is
    // larger than the 40MB threshold. Most phone clips are 120-180MB →
    // transcode brings them down to 25-40MB. This dodges the 100MB
    // nginx cap and means users don't have to manually trim.
    let uploadFile = file;
    if (file.size > PLYX_DUET_COMPRESS_THRESHOLD) {
      if (!isCompressionSupported()) {
        // Old browser that can't transcode — only allow if it's
        // already under the upload cap.
        if (file.size > PLYX_DUET_UPLOAD_CAP) {
          toast.error(
            `Your clip is ${formatMB(file.size)} — too large for upload. Your browser doesn't support in-browser compression. Try Chrome or a newer Safari, or trim to under ${formatMB(PLYX_DUET_UPLOAD_CAP)}.`,
            { duration: 7000 },
          );
          return;
        }
      } else {
        setCompressing(true);
        setCompressProgress(0);
        try {
          const result = await compressVideoForUpload(file, {
            maxBytes: PLYX_DUET_COMPRESS_THRESHOLD, // skip if already small
            onProgress: (p) => setCompressProgress(p),
          });
          uploadFile = result.file;
          if (!result.skipped) {
            const saved = ((1 - result.compressedSize / result.originalSize) * 100).toFixed(0);
            toast.success(
              `Optimised your clip · ${formatMB(result.originalSize)} → ${formatMB(result.compressedSize)} (${saved}% smaller)`,
              { duration: 4000 },
            );
          }
        } catch (err) {
          // iter-119cc: don't kill the upload if compression fails
          // (e.g. Chrome Android on HEVC recordings). Fall back to raw
          // upload if the file still fits within the ingress cap.
          console.warn("[Plyx Duet] compression failed, falling back to raw:", err);
          setCompressing(false);
          if (file.size <= PLYX_DUET_UPLOAD_CAP) {
            uploadFile = file;
            toast.message(
              "Uploading in original quality — server will handle the compression.",
              { duration: 4000 },
            );
          } else {
            toast.error(
              `Your clip is ${formatMB(file.size)} and your browser can't compress this format (likely HEVC). Try a shorter recording, or switch phone camera to "H.264" mode.`,
              { duration: 8000 },
            );
            return;
          }
        }
        setCompressing(false);
      }
    }

    // Defensive final check — if compression somehow yielded an
    // oversize file, surface a clear error rather than letting nginx
    // bounce it with a generic 413.
    if (uploadFile.size > PLYX_DUET_UPLOAD_CAP) {
      toast.error(
        `Even after compression your clip is ${formatMB(uploadFile.size)}. Try a shorter recording.`,
        { duration: 6000 },
      );
      return;
    }

    setUploading(true);
    const form = new FormData();
    form.append("file", uploadFile);
    form.append("caption", caption);
    try {
      const r = await api.post(`/plyx/videos/${videoId}/duet`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        // 8 min ceiling — leaves room for slow uploads on 3G but
        // surfaces a dead connection instead of spinning forever.
        timeout: 8 * 60 * 1000,
      });
      setDuetVideoId(r.data.video_id);
      setDuetStatus(r.data.duet_status || "queued");
    } catch (err) {
      const status = err?.response?.status;
      let msg = err?.response?.data?.detail || "Upload failed";
      if (status === 413) {
        msg = `Your clip is too large for upload (max ${formatMB(PLYX_DUET_UPLOAD_CAP)} after compression). Trim a shorter cut or record at lower quality.`;
      } else if (err?.code === "ECONNABORTED" || /timeout/i.test(err?.message || "")) {
        msg = "Upload timed out — check your connection and try again with a shorter clip.";
      } else if (!err?.response && /Network Error/i.test(err?.message || "")) {
        msg = "Network error — your connection dropped mid-upload. Try again on stronger Wi-Fi.";
      }
      toast.error(msg, { duration: 6000 });
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin opacity-60"/>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6">
        <div className="text-center max-w-md" data-testid="plyx-duet-error">
          <AlertCircle className="w-10 h-10 mx-auto opacity-60 mb-4"/>
          <p className="text-white/75">{error}</p>
          <button onClick={() => navigate("/plyx/feed")} className="mt-6 text-sm text-white/55 underline">
            ← Back to feed
          </button>
        </div>
      </div>
    );
  }

  // ── Composing state ──
  if (duetVideoId) {
    const done = duetStatus === "ready";
    const failed = duetStatus === "failed";
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6" data-testid="plyx-duet-composing">
        <div className="max-w-md text-center">
          {done ? (
            <CheckCircle2 className="w-16 h-16 mx-auto text-white/85 mb-5"/>
          ) : failed ? (
            <AlertCircle className="w-16 h-16 mx-auto text-[#FF2D7A] mb-5"/>
          ) : (
            <div className="relative w-20 h-20 mx-auto mb-5">
              <Sparkles className="w-20 h-20 text-[#FFB42D] animate-pulse" />
            </div>
          )}
          <h1 className="font-display text-3xl mb-3">
            {done ? "Your duet is live!" : failed ? "We couldn't compose this one" : "Composing your duet…"}
          </h1>
          <p className="text-white/55 text-sm mb-6">
            {done
              ? "Redirecting you to the feed — your remix is at the top."
              : failed
              ? "Try a shorter clip (under 60s) or a different format."
              : "Side-by-side compositing usually takes 10–30 seconds. Hang tight."}
          </p>
          {failed && (
            <button
              onClick={() => { setDuetVideoId(null); setDuetStatus(null); setUploading(false); }}
              data-testid="plyx-duet-retry"
              className="px-5 py-2 rounded-full bg-white text-white font-semibold text-sm"
            >
              Try again
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Upload state ──
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-20" data-testid="plyx-duet-page">
      <div className="max-w-4xl mx-auto px-6 pt-10">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] uppercase tracking-[0.18em] font-bold text-white mb-5"
          style={{ background: "linear-gradient(135deg,#00E0C7,#FFB42D)" }}>
          <Layers className="w-3 h-3"/> Duet creator
        </div>
        <h1 className="font-display text-4xl mb-2">Remix this video.</h1>
        <p className="text-white/55 text-sm mb-8">
          Your clip will play side-by-side with the original. 20% of any gifts you earn goes back to{" "}
          <strong className="text-white">@{parent?.creator?.handle}</strong> automatically.
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Original preview */}
          <div className="rounded-xl overflow-hidden border border-white/10 bg-white/[0.03]">
            <div className="aspect-[9/16] bg-black relative">
              {parent?.video_url && (
                <video
                  src={parent.video_url}
                  className="w-full h-full object-cover"
                  controls
                  playsInline
                  data-testid="plyx-duet-parent-preview"
                />
              )}
            </div>
            <div className="p-4">
              <div className="text-[10px] uppercase tracking-wider text-white/50 mb-1.5">Original</div>
              <div className="font-semibold text-sm">@{parent?.creator?.handle}</div>
              <div className="text-xs text-white/55 line-clamp-2 mt-1">{parent?.caption || "—"}</div>
            </div>
          </div>

          {/* Upload form */}
          <form onSubmit={submit} className="rounded-xl border border-white/10 bg-white/[0.03] p-5 flex flex-col" data-testid="plyx-duet-form">
            <div className="text-[10px] uppercase tracking-wider text-white/50 mb-1.5">Your remix clip</div>
            <label className={`block flex-1 rounded-lg border-2 border-dashed ${file ? "border-[#00E0C7]/60" : "border-white/15"} cursor-pointer p-6 text-center transition-colors hover:border-white/35`}>
              <input
                type="file"
                accept="video/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="plyx-duet-file"
                className="hidden"
              />
              {file ? (
                <div>
                  <CheckCircle2 className={`w-8 h-8 mx-auto mb-3 ${file.size > PLYX_DUET_HARD_MAX ? "text-[#FF2D7A]" : "text-white/85"}`}/>
                  <div className="font-semibold text-sm truncate">{file.name}</div>
                  <div className={`text-xs mt-1 ${file.size > PLYX_DUET_HARD_MAX ? "text-[#FF2D7A] font-semibold" : "text-white/45"}`}>
                    {formatMB(file.size)}
                    {file.size > PLYX_DUET_HARD_MAX && ` · Too large (max ${formatMB(PLYX_DUET_HARD_MAX)})`}
                    {file.size > PLYX_DUET_COMPRESS_THRESHOLD && file.size <= PLYX_DUET_HARD_MAX && (
                      <span className="text-[#FFB42D]"> · will be auto-optimised before upload ✨</span>
                    )}
                  </div>
                  <div className="text-[11px] text-white/55 mt-3 underline">Choose a different clip</div>
                </div>
              ) : (
                <div>
                  <Upload className="w-8 h-8 mx-auto text-white/45 mb-3"/>
                  <div className="font-semibold text-sm">Tap to upload your clip</div>
                  <div className="text-xs text-white/45 mt-1">MP4, MOV, or WebM · up to {formatMB(PLYX_DUET_HARD_MAX)} · auto-optimised in your browser</div>
                </div>
              )}
            </label>

            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              maxLength={2200}
              placeholder={`Duet with @${parent?.creator?.handle}`}
              data-testid="plyx-duet-caption"
              rows={3}
              className="mt-4 w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm focus:outline-none focus:border-white/25 text-white placeholder:text-white/35"
            />

            <button
              type="submit"
              disabled={!file || uploading || compressing || (file && file.size > PLYX_DUET_HARD_MAX)}
              data-testid="plyx-duet-submit"
              className="mt-5 w-full px-5 py-3 rounded-full bg-white text-white font-bold text-sm hover:scale-[1.01] transition-transform disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            >
              {compressing ? <Zap className="w-4 h-4 animate-pulse"/> : uploading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Layers className="w-4 h-4"/>}
              {compressing
                ? `Optimising your clip… ${Math.round(compressProgress * 100)}%`
                : uploading
                ? "Uploading…"
                : (file && file.size > PLYX_DUET_HARD_MAX
                    ? "Clip too large — pick a shorter one"
                    : "Create duet")}
            </button>

            {compressing && (
              <div
                className="mt-3 w-full h-1.5 rounded-full bg-white/10 overflow-hidden"
                data-testid="plyx-duet-compress-bar"
              >
                <div
                  className="h-full transition-all"
                  style={{
                    width: `${Math.max(2, Math.round(compressProgress * 100))}%`,
                    background: "linear-gradient(90deg, #FFB42D, #00E0C7)",
                  }}
                />
              </div>
            )}
            {compressing && (
              <p className="mt-2 text-[11px] text-white/55 text-center">
                Re-encoding in your browser so the upload fits — usually 15-30s.
              </p>
            )}

            <p className="mt-3 text-[11px] text-white/35 text-center">
              By posting, you agree the original creator earns 20% of any gifts on this duet.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PlyxDuet;
